import { useMemo } from 'react';
import type { Exercise, EffortScale, WeeklyTargetRow } from '@jackedlogs/shared';
import {
  computeInWorkoutTargets,
  programWeeklyPrescription,
} from '@jackedlogs/shared';

import type { LoggerDraft } from './state/loggerDraftTypes';
import { useSessionResults } from '../index';
import { useInWorkoutMeterEnabled } from './state/inWorkoutMeterPreference';
import { buildPayload } from './workoutMapping';
import type { LoggerHostDeps } from './hostDeps';

// AP-3b — the in-workout contextual meter's data source. Scores the muscles the
// ACTIVE exercise trains against the pinned program's weekly targets, with THIS
// session's just-logged sets already folded in (computeInWorkoutTargets does the
// fold via the shared computeSessionResult → computeWeeklyTargets path). Mirrors
// PlanScreen's Home wiring: session results (cache-first SWR) + the pinned
// "active block" program; adds only the live-draft fold on top. Returns [] until
// the resources land or when nothing is contextual — the strip hides itself.
//
// RW-18: `sessionResultsDeps` is injected (both hosts already build one —
// mobile's apps/mobile/src/progress/progressDeps.ts, web-rnw's
// apps/web-rnw/src/progress/progressDeps.ts — useSessionResults itself was
// already portable/shared). `followedPrograms`, `profile` and
// `athleteTargets` are host-resolved DATA rather than called hooks:
// mobile's copies are its own app-wide singletons/caches (and, for
// athleteTargets specifically, a DELIBERATELY un-repointed duplicate of
// the shared hook — see hostDeps.ts's doc — so this move must not
// silently fix that debt as a side effect).
export function useInWorkoutTargets(
  draft: LoggerDraft,
  activeExercise: Exercise | null,
  catalog: Exercise[] | null,
  effortScale: EffortScale,
  deps: LoggerHostDeps,
): WeeklyTargetRow[] {
  const { enabled } = useInWorkoutMeterEnabled();
  const { results: sessionResults } = useSessionResults(deps.sessionResultsDeps);
  const { profile, followedPrograms, athleteTargets } = deps;

  const pinnedProgram = useMemo(
    () =>
      followedPrograms.find((p) => p.id === profile.currentProgramId) ?? null,
    [followedPrograms, profile.currentProgramId],
  );
  const bodyweightKg = profile.bodyweightKg ?? 0;

  // AP-9 — the program's per-muscle weekly hard-set prescription, memoized on
  // [program, catalog] (same idiom as the composer's ProgramComposer memo) so
  // the full-cycle walk doesn't rerun on every logged set; only [program,
  // catalog] change it. Feeds the strip's "no coverage" vs "building" distinction.
  const prescription = useMemo(
    () =>
      pinnedProgram && catalog
        ? programWeeklyPrescription(pinnedProgram, catalog)
        : null,
    [pinnedProgram, catalog],
  );

  // A signature of only the COMPLETED sets — the sole draft state the fold reads
  // (buildPayload drops uncompleted sets before we ever see them). Typing in the
  // still-pending "next set" input leaves this unchanged, so the expensive fold
  // below doesn't rerun on every keystroke.
  const completedSig = useMemo(
    () =>
      draft.entries
        .map(
          (e) =>
            e.exerciseId +
            ':' +
            e.sets
              .filter((s) => s.completedAt !== null)
              .map(
                (s) =>
                  `${s.reps}x${s.weight ?? ''}r${s.rir ?? ''}f${s.fail ? 1 : 0}d${s.duration ?? ''}`,
              )
              .join(','),
        )
        .join('|'),
    [draft],
  );

  return useMemo(
    () => {
      if (!enabled || !activeExercise || catalog === null || sessionResults === null) {
        return [];
      }
      const built = buildPayload(draft, effortScale, { dropUncompleted: true });
      const draftWorkout = built.ok ? built.payload : null;
      return computeInWorkoutTargets(
        sessionResults,
        draftWorkout,
        catalog,
        pinnedProgram,
        activeExercise,
        bodyweightKg,
        Date.now(),
        prescription,
        profile.weekStartDay,
        athleteTargets,
      );
    },
    // `draft` is intentionally omitted: completedSig captures the completed-set
    // state the fold depends on, so keying on the whole draft (a fresh object
    // each keystroke) would recompute the fold pointlessly on pending edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      enabled,
      completedSig,
      sessionResults,
      pinnedProgram,
      prescription,
      catalog,
      activeExercise,
      bodyweightKg,
      effortScale,
      profile.weekStartDay,
      athleteTargets,
    ],
  );
}
